/**
 * « Ce que ton parent t'a préparé » — en tête du travail du soir.
 *
 * Demandé par Hedi le 24 septembre 2026, comme *« une entrée séparée en tête »*
 * de l'écran de l'enfant, distincte du plan du soir automatique. La distinction
 * n'est pas d'affichage : tout le reste de cet écran est une proposition du
 * système, calculée ; ceci est une liste d'exercices qu'un adulte a cochés un
 * par un dans un cahier qu'il a sous la main. Les mélanger ferait de la seconde
 * une suggestion de plus, déclinable comme les autres.
 *
 * **Elle passe avant la carte du cahier de textes**, elle-même en tête de tout
 * ce que nous proposons. Deux raisons, et la seconde décide : c'est la seule
 * chose de l'écran qui attend quelque chose *en retour* de l'enfant ce soir, et
 * elle n'occupe rien les soirs où elle n'existe pas — le serveur ne rend que les
 * séries incomplètes, donc la carte disparaît d'elle-même et ne repousse jamais
 * le devoir du professeur vers le bas sans raison.
 *
 * **L'enfant lit à l'écran, travaille sur son cahier, et photographie.** Une
 * photo par exercice : c'est ce découpage qui permet au parent de juger exercice
 * par exercice au lot suivant, et c'est aussi pourquoi la série ne porte aucune
 * réponse saisie — l'énoncé est une page d'ouvrage, pas une question à trou.
 *
 * **La photo est le critère d'achèvement, pas le verdict.** Une série
 * entièrement photographiée quitte cet écran même si le parent n'a encore rien
 * relu : attendre la relecture reviendrait à redemander à l'enfant un travail
 * qu'il a rendu.
 *
 * **Le corrigé de l'éditeur ne traverse jamais cette route** (règle 2) : il est
 * servi au parent par un chemin de signature distinct (`web-pwa` §17.4), et un
 * test l'affirme sur le texte HTTP brut des deux côtés.
 *
 * La photo part directement au stockage par URL présignée ; seule sa clé
 * traverse l'API (invariant 7), et les vignettes sont servies sous URL signée à
 * durée de vie courte (règle 8).
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import type { EntrainementOut, LigneDEntrainementOut } from "@/api/schema";
import { uploadPhoto } from "@/photo/upload";
import { EnonceImages, imagesDeLEnonce } from "@/ui/EnonceImage";
import { EtiquetteParascolaire } from "@/ui/Parascolaire";
import { Button, Card, ErrorNote } from "@/ui/primitives";
import { parChapitre } from "@/ui/feuille";

type Etat =
  { quoi: "chargement" } | { quoi: "panne" } | { quoi: "pret"; entrainements: EntrainementOut[] };

export function EntrainementDuParent() {
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });
  // Vrai seulement après avoir vu une série et l'avoir vue disparaître : sans
  // cela, la dernière photo envoyée escamote la carte sans un mot, et l'enfant
  // ne sait pas si elle est partie.
  const [acheve, setAcheve] = useState(false);

  const charger = useCallback(async () => {
    try {
      const reponse = await call("mesEntrainements");
      const entrainements = reponse.entrainements ?? [];
      setEtat((avant) => {
        const avait = avant.quoi === "pret" && avant.entrainements.length > 0;
        if (avait && entrainements.length === 0) setAcheve(true);
        return { quoi: "pret", entrainements };
      });
    } catch {
      setEtat({ quoi: "panne" });
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Tant qu'elle charge, elle n'occupe rien : le travail du soir ne doit pas
  // attendre une carte qui, la plupart des soirs, est vide.
  if (etat.quoi === "chargement") return null;

  if (etat.quoi === "panne") {
    return (
      <Card title="Ce que ton parent t'a préparé">
        <ErrorNote>Je n'arrive pas à lire ce qui a été préparé pour toi.</ErrorNote>
        <Button variant="quiet" onClick={() => void charger()}>
          Réessayer
        </Button>
      </Card>
    );
  }

  if (etat.entrainements.length === 0) {
    return acheve ? (
      <Card title="Ce que ton parent t'a préparé">
        <p className="muted small">
          Tout est envoyé. Ton parent va regarder avec la correction du cahier.
        </p>
      </Card>
    ) : null;
  }

  return (
    <Card title="Ce que ton parent t'a préparé">
      <p className="muted small">
        Fais ces exercices sur ton cahier, puis photographie chacun d'eux.
      </p>
      {etat.entrainements.map((entrainement) => (
        <Entrainement key={entrainement.id} entrainement={entrainement} rafraichir={charger} />
      ))}
    </Card>
  );
}

function Entrainement({
  entrainement,
  rafraichir,
}: {
  entrainement: EntrainementOut;
  rafraichir: () => Promise<void>;
}) {
  const lignes = entrainement.lignes ?? [];
  const rendues = entrainement.photographiees ?? 0;

  // Une feuille traverse plusieurs chapitres depuis le 25 septembre 2026 : le
  // titre descend sur chaque groupe, et l'enfant retrouve la page du cahier au
  // bon endroit plutôt qu'un seul titre pour toute la liste.
  return (
    <section>
      <p className="muted small">
        {rendues} photographié{rendues > 1 ? "s" : ""} sur {lignes.length}
      </p>
      {parChapitre(lignes).map((bloc) => (
        <div key={`${bloc.ouvrageTitre}/${bloc.chapitre}`}>
          <p className="devoirs__entete">
            <strong>{bloc.titre}</strong>
            <span className="muted small"> · {bloc.ouvrageTitre}</span>
          </p>
          <ul className="devoirs__liste">
            {bloc.lignes.map((ligne) => (
              <Ligne
                key={ligne.ligne_id}
                ligne={ligne}
                ouvrage={bloc.ouvrageTitre}
                rafraichir={rafraichir}
              />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

type Envoi = "repos" | "envoi" | "panne";

function Ligne({
  ligne,
  ouvrage,
  rafraichir,
}: {
  ligne: LigneDEntrainementOut;
  /** Le titre du cahier, répété sur chaque énoncé : l'étiquette accompagne
   *  l'image partout où elle est rendue, et un test balaie la source pour
   *  l'affirmer. Le titre entier nomme un objet posé sur la table ; l'en-tête de
   *  la série, lui, se perd dès qu'on a fait défiler deux exercices. */
  ouvrage: string;
  rafraichir: () => Promise<void>;
}) {
  const [envoi, setEnvoi] = useState<Envoi>("repos");
  const enonces = imagesDeLEnonce(ligne.enonce_urls, ligne.enonce_url);

  const photographier = async (file: File) => {
    setEnvoi("envoi");
    try {
      const photo = await uploadPhoto(file);
      await call("photographierUnEntrainement", {
        params: { ligne_id: ligne.ligne_id },
        body: { key: photo.key },
      });
      // On relit la série plutôt que de poser la vignette de notre côté :
      // l'URL est signée, et c'est le serveur qui la délivre.
      await rafraichir();
      setEnvoi("repos");
    } catch {
      setEnvoi("panne");
    }
  };

  const retirer = async () => {
    setEnvoi("envoi");
    try {
      await call("retirerLaPhotoDEntrainement", { params: { ligne_id: ligne.ligne_id } });
      await rafraichir();
      setEnvoi("repos");
    } catch {
      setEnvoi("panne");
    }
  };

  return (
    <li className="devoirs__ligne">
      <p className="devoirs__entete">
        <strong>{ligne.numero ? `Exercice ${ligne.numero}` : `Exercice ${ligne.rang}`}</strong>
        {ligne.page ? <span className="muted small"> · page {ligne.page}</span> : null}
      </p>

      {/* Une forme sur cahier (lot P3) : la phrase « sur ton cahier », la
          consigne quand l'exercice est un texte (une opération calculée, la
          consigne d'une dictée) et la durée **en mots**. Le texte d'une dictée
          n'est jamais ici — le signataire de l'enfant ne le remplit pas. */}
      {ligne.consigne_enfant ? <p className="small">{ligne.consigne_enfant}</p> : null}
      {ligne.consigne ? <p>{ligne.consigne}</p> : null}
      {ligne.duree_annoncee ? (
        <p className="muted small">Prévois {ligne.duree_annoncee}.</p>
      ) : null}

      {/* L'énoncé est la page du cahier, photographiée : elle se lit en zoomant,
          comme un PDF. Le texte n'existe pas de notre côté — un ouvrage est
          découpé en images —, donc `EnonceImage` n'a rien à annoncer d'autre. */}
      {enonces.length ? (
        <>
          <EtiquetteParascolaire nom={ouvrage} />
          <EnonceImages images={enonces} statement="" />
        </>
      ) : null}

      {ligne.photo_url ? (
        <ul className="devoirs__photos">
          <li>
            <a href={ligne.photo_url} target="_blank" rel="noreferrer">
              <img
                className="devoirs__photo"
                src={ligne.photo_url}
                alt="Ta réponse photographiée"
                loading="lazy"
              />
            </a>
            <Button variant="quiet" disabled={envoi === "envoi"} onClick={() => void retirer()}>
              Reprendre la photo
            </Button>
          </li>
        </ul>
      ) : (
        <label className="devoirs__prise">
          <span className="muted small">Photographie ta réponse sur le cahier.</span>
          <input
            className="input"
            type="file"
            accept="image/*"
            capture="environment"
            disabled={envoi === "envoi"}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void photographier(file);
            }}
          />
        </label>
      )}

      {envoi === "envoi" ? <p className="muted small">Envoi…</p> : null}
      {envoi === "panne" ? <ErrorNote>La photo n'est pas partie. Réessaie.</ErrorNote> : null}
    </li>
  );
}
